import type { QueryClient, QueryKey } from '@tanstack/react-query';
import { useLayoutEffect, useRef } from 'react';

/** Existing rendered callbacks retire synchronously, including denial and recovery between frames. */
export function useInboxPreviewFence(
  client: QueryClient,
  key: QueryKey,
  data: unknown,
  scopeCurrent: () => boolean,
  onRetire?: (denied: boolean) => void,
) {
  const token = useRef({ data, retired: false });
  const retired = useRef(onRetire);
  retired.current = onRetire;
  const live = () => {
    const state = client.getQueryState(key);
    return Boolean(
      scopeCurrent() &&
      navigator.onLine !== false &&
      data &&
      state &&
      state.data === data &&
      !state.error &&
      !state.isInvalidated &&
      state.fetchStatus === 'idle',
    );
  };
  if (token.current.data !== data || (token.current.retired && live())) token.current = { data, retired: false };
  const captured = token.current;
  const keyIdentity = JSON.stringify(key);
  useLayoutEffect(() => {
    const retire = (denied = false) => {
      if (!token.current.retired || denied) retired.current?.(denied);
      token.current.retired = true;
    };
    const unsubscribe = client.getQueryCache().subscribe(() => {
      const state = client.getQueryState(key);
      if (
        !state ||
        state.data !== token.current.data ||
        state.error ||
        state.isInvalidated ||
        state.fetchStatus !== 'idle'
      )
        retire(Boolean(state?.error) || !state);
    });
    const reconnect = () => {
      if (scopeCurrent()) void client.invalidateQueries({ queryKey: key });
    };
    const offline = () => retire();
    window.addEventListener('offline', offline);
    window.addEventListener('online', reconnect);
    return () => {
      token.current.retired = true;
      unsubscribe();
      window.removeEventListener('offline', offline);
      window.removeEventListener('online', reconnect);
    };
  }, [client, keyIdentity]);
  return () => token.current === captured && !captured.retired && live();
}

/** Capture before any consent wait. A denied, replaced or offline context stays retired. */
export function captureInboxPreviewLease(
  client: QueryClient,
  key: QueryKey,
  data: unknown,
  scopeCurrent: () => boolean,
) {
  let retired = false;
  const check = () => {
    const state = client.getQueryState(key);
    if (
      !scopeCurrent() ||
      navigator.onLine === false ||
      !data ||
      !state ||
      state.data !== data ||
      state.error ||
      state.isInvalidated ||
      state.fetchStatus !== 'idle'
    )
      retired = true;
    return !retired;
  };
  const unsubscribe = client.getQueryCache().subscribe(() => {
    check();
  });
  const retire = () => {
    retired = true;
  };
  window.addEventListener('offline', retire);
  check();
  return {
    current: check,
    dispose: () => {
      retired = true;
      unsubscribe();
      window.removeEventListener('offline', retire);
    },
  };
}
